<template>
  <div class="calendar select-none">
    <div class="flex items-center justify-between mb-2 -mx-1">
      <button
        type="button"
        :tabindex="getTabIndex"
        class="button is-gray-100 calendar-button p-1"
        @click="switchNavMonth(-1)"
        :disabled="switchMonth || switchYear"
      >préc.</button>
      <div class="flex items-center justify-center">
        <button
          type="button"
          :tabindex="getTabIndex"
          class="button calendar-button p-1"
          @click="switchMonth = !switchMonth; switchYear = false"
        >{{ navMonth }}</button>
        <button
          type="button"
          :tabindex="getTabIndex"
          class="button calendar-button p-1 ml-1"
          @click="switchYear = !switchYear; switchMonth = false"
        >{{ navYear }}</button>
      </div>
      <button
        type="button"
        :tabindex="getTabIndex"
        class="button is-gray-100 calendar-button p-1"
        @click="switchNavMonth(1)"
        :disabled="switchMonth || switchYear"
      >suiv.</button>
    </div>
    <div class="flex flex-wrap -mx-1" v-if="switchMonth">
      <div class="p-1 w-1/3" v-for="(month, index) in months" :key="index">
        <button
          type="button"
          :tabindex="getTabIndex"
          class="button calendar-button p-3 w-full"
          @click.stop="navToMonth(index)"
        >{{ month }}</button>
      </div>
    </div>
    <div class="dropdown overflow-y-auto flex flex-wrap -mx-1" v-if="switchYear">
      <div class="p-1 w-1/3" v-for="(year, index) in years" :key="index">
        <button
          type="button"
          :tabindex="getTabIndex"
          class="button calendar-button p-4 w-full"
          @click.stop="navToYear(year)"
        >{{ year }}</button>
      </div>
    </div>
    <template v-if="!switchYear && !switchMonth">
      <div class="flex mb-2">
        <span
          class="calendar-cell-wrapper text-gray-700 uppercase text-xxs font-bold"
          v-for="(day, index) in weekDaysHeader"
          :key="index"
        >{{ day }}</span>
      </div>
      <div class="flex flex-wrap">
        <div class="calendar-cell-wrapper" v-for="(day, index) in grid" :key="index">
          <button
            type="button"
            :tabindex="getDayTabIndex(day)"
            @click="selectDay(day)"
            @mouseenter="setHoverDay(day)"
            class="calendar-cell block w-full rounded-full focus:shadow-outline focus:outline-none"
            z-index
            :class="{
              'opacity-50 pointer-events-none': isDisabled(day),
              'font-bold': isToday(day),
              'hover:bg-gray-200': !isStart(day) && !isEnd(day) && !isInRange(day) && (!isHoverEnd(day) || startUnset),
              'bg-blue-800 text-white': isStart(day) || (isEnd(day) && startUnset) || isInRange(day) || (isHoverEnd(day) && !startUnset),
              'rounded-none': isInRange(day),
              'rounded-r-none': isStart(day) && range && (startUnset || hoverDay),
              'rounded-l-none': isEnd(day) || (isHoverEnd(day) && !startUnset),
            }"
            v-if="day.value != 0"
          >{{ day.value }}</button>
        </div>
      </div>
      <button type="button" :tabindex="getTabIndex" class="button is-gray-100 calendar-button p-2 text-sm w-full mt-1" @click.stop="navToToday()">Aujourd'hui</button>
    </template>
  </div>
</template>

<script>
import moment from "moment";
import { isMobile } from "../helpers/utils";

export default {
  model: {
    prop: "startDate",
    event: "change"
  },
  props: {
    startDate: String,
    endDate: String,
    limit: {
      type: String,
      default: moment()
        .add(1, "month")
        .format()
    },
    lang: {
      type: String,
      default: "fr"
    },
    format: {
      type: String,
      default: "YYYY-MM-DD"
    },
    enableLimit: {
      type: Boolean,
      default: false
    },
    disablePassedDates: {
      type: Boolean,
      default: false
    },
    disableWeekends: {
      type: Boolean,
      default: false
    },
    range: {
      type: Boolean,
      default: false
    },
    tabbable: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      grid: [],
      weekDaysHeader: [],
      today: moment(),
      navMonth: "",
      navYear: "",
      switchMonth: false,
      switchYear: false,
      months: [],
      years: [],
      hoverDay: null,
      startUnset: true
    };
  },
  computed: {
    getTabIndex() {
      return this.tabbable ? 0 : -1;
    },
    start: {
      get() {
        return this.startDate ? moment(this.startDate) : null;
      },
      set(value) {
        this.$emit("change", value.format(this.format));
      }
    },
    end: {
      get() {
        return this.endDate ? moment(this.endDate) : null;
      },
      set(value) {
        this.$emit("end-date-change", value.format(this.format));
      }
    },
    navDate: {
      get() {
        return this.startDate ? moment(this.startDate) : moment();
      },
      set(value) {
        this.setTitle(value);
        this.setGrid(value);
      }
    }
  },
  created() {
    moment.locale(this.lang);
    this.setHeader();
    this.setGrid();
    this.setTitle();
    this.setMonths();
    this.setYears();
  },
  methods: {
    setHeader() {
      const weekDaysArr = [0, 1, 2, 3, 4, 5, 6];
      weekDaysArr.forEach(day => {
        this.weekDaysHeader.push(
          moment()
            .weekday(day)
            .format("ddd")
        );
      });
    },
    setTitle(date = this.navDate) {
      this.navMonth = date.format("MMM");
      this.navYear = date.format("YYYY");
    },
    setGrid(date = this.navDate) {
      this.grid = [];

      const firstDayDate = date.startOf("month");
      const initialEmptyCells = firstDayDate.weekday();
      const lastDayDate = date.endOf("month");
      const lastEmptyCells = 6 - lastDayDate.weekday();
      const daysInMonth = date.daysInMonth();
      const arrayLength = initialEmptyCells + lastEmptyCells + daysInMonth;

      for (let i = 0; i < arrayLength; i++) {
        let day = {};
        if (i < initialEmptyCells || i > initialEmptyCells + daysInMonth - 1) {
          day.value = 0;
          day.disabled = true;
        } else {
          day.value = i - initialEmptyCells + 1;
          day.disabled = this.disableWeekends
            ? (i + 1) % 7 === 0 || (i + 2) % 7 === 0
            : false;
        }
        this.grid.push(day);
      }
    },
    setMonths() {
      this.months = moment.monthsShort();
    },
    setYears() {
      let currentYear = moment().year();
      for (let year = currentYear + 1; year > currentYear - 100; year--) {
        this.years.push(year);
      }
    },
    setYears() {
      let currentYear = moment().year();
      for (let year = currentYear + 1; year > currentYear - 100; year--) {
        this.years.push(year);
      }
    },
    switchNavMonth(qty) {
      this.navDate = moment(this.navDate.add(qty, "month"));
    },
    navToMonth(index) {
      this.navDate = moment(this.navDate.month(index));
      this.switchMonth = false;
    },
    navToYear(year) {
      this.navDate = moment(this.navDate.year(year));
      this.switchYear = false;
    },
    navToToday() {
      this.navDate = moment(this.navDate.year(moment().year()).month(moment().month()))
    },
    selectDay(day) {
      const date = this.getDateFromNum(day.value);
      if (this.range) {
        this.hoverDay = null;
        if (this.startUnset) {
          this.startUnset = false;
          this.start = date;
        } else if (!date.isSame(this.start)) {
          const isDateAfterStart = date.isAfter(this.start);
          this.end = isDateAfterStart ? date : this.start;
          this.start = isDateAfterStart ? this.start : date;
          this.startUnset = true;
        }
      } else {
        this.start = date;
      }
    },
    isDisabled(day) {
      let dateToCheck = this.getDateFromNum(day.value);
      if (this.disablePassedDates && dateToCheck.isSameOrBefore(moment())) {
        return true || day.disabled;
      } else if (
        this.enableLimit &&
        dateToCheck.isAfter(moment(this.limit).format())
      ) {
        return true;
      }
      return day.disabled;
    },
    isSelected(day) {
      if (moment.isMoment(this.start)) {
        return this.start.isSame(this.navDate, "month") &&
          this.start.isSame(this.navDate, "year")
          ? day.value === this.start.date()
          : false;
      }
      return false;
    },
    isStart(day) {
      return this.isActive(day, this.start);
    },
    isHoverEnd(day) {
      return this.isActive(day, this.hoverDay);
    },
    isEnd(day) {
      return this.isActive(day, this.end);
    },
    setHoverDay(day) {
      if (this.range) {
        const start = this.start;
        const hover = this.getDateFromNum(day.value);

        if (moment.isMoment(start) && start.isBefore(hover)) {
          this.hoverDay = hover;
        } else {
          this.hoverDay = null;
        }
      }
    },
    isActive(day, comparison) {
      if (
        moment.isMoment(comparison) &&
        comparison.isSame(this.navDate, "month") &&
        comparison.isSame(this.navDate, "year")
      ) {
        return day.value === comparison.date();
      }
      return false;
    },
    isInRange(day) {
      let start = this.start;
      let end = this.end && this.startUnset ? this.end : this.hoverDay;

      if (moment.isMoment(start)) {
        if (moment.isMoment(end) && start.isSameOrBefore(end, "year")) {
          if (
            start.isSame(end, "month") &&
            start.isSame(this.navDate, "month") &&
            start.isSame(this.navDate, "year")
          ) {
            return day.value > start.date() && day.value < end.date();
          } else if (start.isBefore(end, "month")) {
            if (
              start.isSame(this.navDate, "month") &&
              start.isSame(this.navDate, "year")
            ) {
              return day.value > start.date();
            } else if (
              end.isSame(this.navDate, "month") &&
              end.isSame(this.navDate, "year")
            ) {
              return day.value < end.date();
            } else if (
              start.isBefore(this.navDate) &&
              end.isAfter(this.navDate)
            ) {
              return true;
            }
          }
        }
      }

      return false;
    },
    isToday(day) {
      return (
        this.today.isSame(this.navDate, "month") &&
        this.today.isSame(this.navDate, "year") &&
        this.today.date() === day.value
      );
    },
    getDateFromNum(num) {
      let returnDate = moment(this.navDate);
      return returnDate.date(num);
    },
    getDayTabIndex(day) {
      return this.tabbable && !this.isDisabled(day) ? 0 : -1;
    }
  }
};
</script>

<style scoped>
.rounded-none {
  border-radius: 0 !important;
}
.calendar {
  width: 277px;
}
.calendar-cell-wrapper {
  width: 35px;
}
.calendar-cell {
  width: 100%;
  height: 35px;
}
.calendar-button {
  @apply rounded;
  &:hover {
    @apply bg-gray-200;
  }
  &:focus {
    @apply shadow-outline;
  }
}
input[type="date"]::-webkit-inner-spin-button,
input[type="date"]::-webkit-calendar-picker-indicator,
input[type="date"]::-webkit-clear-button {
  display: none;
  -webkit-appearance: none;
}
</style>
